import * as React from "react";
import type { GetStaticProps } from "next";
import Layout from "../../components/Layout";
import { getSiteSettings } from "../../routes/common";
import type { SeoMeta } from "../../types/generated/schema";
import FsLightbox from "fslightbox-react";
import * as api from "property-api/openapi";
import Heading from "@components/heading";
import Container from "@components/container";
import { stringify } from "query-string";
import PropositionContactForm from "@components/proposition-contact-form";
import { PropertyDescriptiveDetails } from "@components/property-descriptive-details";
import { TabButton, Scroller } from "@components/tabs";
import ImageSnapSlider from "@components/image-snap-slider";
import CollapsibleArea from "@components/collapsible-area/collapsible-area";
import { AnchorButton } from "@components/anchor-button";
import {
	FloorPlanIcon,
	LocationMarkerIcon,
	ObjectSpaceIcon,
	VideoIcon,
	CalendarIcon,
	BedIcon,
	PriceTagIcon,
	DoorIcon,
} from "icons/components";
import TooltipButton from "@components/tooltip-button";
import { Status } from "@/routes/properties";
import { useWindowSize } from "@/utils/animation";
import { getFormTitle, truncateDescription } from "@/utils/copy";
import LocationMap from "@components/sections/location-map";

function PropositionDetailPage({
	pageData,
	googleMapsLink,
	panoramaLink,
	config,
}: {
	pageData: api.Property;
	heroImage: string | null;
	googleMapsLink: string;
	panoramaLink: string | null;
	config: any;
}) {
	const [showVideo, setShowVideo] = React.useState(false);
	const size = useWindowSize();
	const [showFloorplan, setShowFloorplan] = React.useState(false);
	const [showMap, setShowMap] = React.useState(false);
	const [showPanorama, setShowPanorama] = React.useState(false);
	const seoTags: SeoMeta = {
		_type: "seoMeta",
		title: pageData.title ?? "",
		description: truncateDescription(pageData.description ?? ""),
	};

	// @ts-ignore
	let floorPlans = pageData.media.filter(
		(a) => a.soort === "PLATTEGROND" && a.mimetype === "image/jpeg",
	);

  let floorPlanPdf = false;

	if(floorPlans.length === 0) {
	 // @ts-ignore
		floorPlans = pageData.media.filter(
			(a) => a.soort === "PLATTEGROND" && a.mimetype === "application/pdf",
		);
		floorPlanPdf = true;
	}
	// @ts-ignore
	const videos = pageData.media.filter((a) => a.soort === "VIDEO");
	// @ts-ignore
	pageData.media.sort((a, b) => (a.soort === "HOOFDFOTO" ? -1 : 1));

	const hasTabsBar =
		floorPlans.length > 0 || videos.length > 0 || googleMapsLink !== "";

	return (
		// @ts-ignore
		<Layout config={config} tags={seoTags}>
			<div className="pt-20 bg-gray-100 header-card">
				<Container>
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-10">
						<div className="col-span-1 space-y-8 lg:col-span-4">
							{!!pageData.readableStatus &&
								pageData.status !== Status.AVAILABLE && (
									<div className="inline-flex">
										<div className="p-4 font-bold text-black bg-primary rounded-xl">
											{pageData.readableStatus}
										</div>
									</div>
								)}
							<div>
								<Heading className="text-2xl font-rigBold" as="h3">
									{pageData.title ?? ""}
								</Heading>
							</div>

							{!!pageData.formattedPrice && (
								<div className="inline-flex items-center p-4 bg-primaryLight rounded-xl">
									<PriceTagIcon className="w-6 h-6" />
									<p className="ml-2 text-lg font-bold border-b border-black border-dashed">
										{pageData.formattedPrice}
									</p>
								</div>
							)}
							<div className="flex flex-row flex-wrap gap-4">
								{!!pageData.plot &&
									Number(pageData.plot) > 0 &&
									pageData.plot !== undefined &&
									pageData.plot !== null && (
										<TooltipButton
											icon={<ObjectSpaceIcon className="w-6 h-6" />}
											buttonContent={
												<p>
													{pageData.plot}{" "}
													<span className="ordinal">
														m<sub className="-top-1">2</sub>
													</span>
												</p>
											}
											tooltipContent="Oppervlakte"
										/>
									)}
								{!!pageData.rooms && Number(pageData.rooms) > 0 && (
									<TooltipButton
										icon={<DoorIcon className="w-6 h-6" />}
										buttonContent={<p>{pageData.rooms}</p>}
										tooltipContent="Kamers"
									/>
								)}
								{!!pageData.bedrooms && Number(pageData.bedrooms) > 0 && (
									<TooltipButton
										icon={<BedIcon className="w-6 h-6" />}
										buttonContent={<p>{pageData.bedrooms}</p>}
										tooltipContent="Slaapkamers"
									/>
								)}
								{!!pageData.buildYear && (
									<TooltipButton
										icon={<CalendarIcon className="w-6 h-6" />}
										buttonContent={<p>{pageData.buildYear}</p>}
										tooltipContent="Bouwjaar"
									/>
								)}
							</div>
							<div className="inline-flex">
								{googleMapsLink !== "" && (
									<AnchorButton
										href={`https://maps.google.com/?q=${pageData.lat},${pageData.lng}`}
										external={true}
										variant="black"
									>
										Bekijk op Google maps
									</AnchorButton>
								)}
							</div>
						</div>
						<div className="col-span-1 lg:col-span-8">
							{pageData.media && pageData.media.length > 0 && (
								<ImageSnapSlider
									images={
										pageData.media.filter(
											(x) => x.soort === "FOTO" || x.soort === "HOOFDFOTO",
										) as any
									}
								/>
							)}
						</div>
					</div>
				</Container>
			</div>
			{hasTabsBar && (
				<div className="flex flex-col px-6 m-auto border-b-2 border-primary max-w-7xl">
					<Scroller>
						{floorPlans.length > 0 && (
							<TabButton onClick={() => floorPlanPdf ?  window.open(floorPlans[0].link, "_blank") : setShowFloorplan(!showFloorplan)}>
								<FloorPlanIcon className="w-5 h-5 mr-2 pointer-events-none" />{" "}
								Bekijk plattegrond
							</TabButton>
						)}
						{videos.length > 0 && (
							<TabButton onClick={() => setShowVideo(!showVideo)}>
								<VideoIcon className="w-5 h-5 mr-2 pointer-events-none" />
								Bekijk video{videos.length > 1 ? "s" : ""}
							</TabButton>
						)}
						{googleMapsLink !== "" && (
							<TabButton onClick={() => setShowMap(!showMap)}>
								<LocationMarkerIcon className="w-5 h-5 mr-2 pointer-events-none" />
								Bekijk op kaart
							</TabButton>
						)}
						{panoramaLink && panoramaLink !== "" && (
							<TabButton onClick={() => setShowPanorama(!showPanorama)}>
								<LocationMarkerIcon className="w-5 h-5 mr-2 pointer-events-none" />
								Toon 360 graden foto
							</TabButton>
						)}
					</Scroller>
				</div>
			)}
			<Container>
				{floorPlans.length > 0 && (
					<FsLightbox toggler={showFloorplan} sources={floorPlans.map((x) => x.link)} />
				)}
				{videos.length > 0 && (
					<FsLightbox
						toggler={showVideo}
						sources={videos.map((video) => `${video.link}&resize=4`)}
						type="video"
					/>
				)}
				{googleMapsLink !== "" && (
					<FsLightbox
						toggler={showMap}
						sources={[
							<iframe
								title="kaart"
								width={size.width - 40 ?? 700}
								height={size.height / 1.2 ?? 450}
								src={googleMapsLink}
							/>,
						]}
					/>
				)}

				<div className="mx-auto my-0">
					<div className="grid grid-cols-1 gap-6 mt-10 lg:my-10 lg:grid-cols-2 lg:gap-10">
						<div>
							<Heading className="mb-6 text-2xl font-rigBold" as="h3">
								{pageData.title ?? ""}
							</Heading>
							{!!pageData.description && (
								<CollapsibleArea>
									{pageData.description.split("\n").map((item, i) => {
										return (
											<p
												key={`description-${i}`}
												dangerouslySetInnerHTML={{ __html: item }}
											/>
										);
									})}
								</CollapsibleArea>
							)}

							<PropertyDescriptiveDetails item={pageData} />
						</div>
						<PropositionContactForm
							title={getFormTitle(pageData.status)}
							subject={pageData.title ?? `id: ${pageData.externalId}`}
						/>
					</div>

					{!!pageData.lat && !!pageData.lng && (
						<LocationMap
							location={{
								_type: "geopoint",
								alt: 0,
								lat: pageData.lat,
								lng: pageData.lng,
							}}
						/>
					)}
				</div>
			</Container>
		</Layout>
	);
}

export default PropositionDetailPage;

export const getStaticProps: GetStaticProps = async ({ params }) => {
	const apiConfig = new api.Configuration({
		basePath: new URL(process.env.NEXT_PUBLIC_PROPERTY_API).origin,
	});
	const q = new api.PropertyApi(apiConfig);
	const id = (params.slug.toString() ?? "").split("-").pop();

	if (Number.isNaN(Number.parseInt(id, 10))) {
		return {
			notFound: true,
		};
	}

	let pageData: api.PropertyJsonld | null = null;
	try {
		pageData = await q.apiPropertiesExternalIdGet({ id });
	} catch (e) {
		return {
			notFound: true,
		};
	}

	const config = await getSiteSettings();

	if (!pageData || !pageData.id || !config) {
		return {
			notFound: true,
		};
	}

	let googleMapsLink = "";
	if (
		pageData.address &&
		!Number.isNaN(pageData.lat) &&
		!Number.isNaN(pageData.lng) &&
		pageData.lat !== undefined &&
		pageData.lng !== undefined
	) {
		googleMapsLink = `https://www.google.com/maps/embed/v1/place?key=${
			process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
		}&center=${pageData.lat},${pageData.lng}&zoom=15&${stringify({
			q: pageData.address,
		})}`;
	}

	return {
		props: {
			pageData: JSON.parse(JSON.stringify(pageData)),
			googleMapsLink,
			config,
		},
		revalidate: 1800,
	};
};

export const getStaticPaths = async () => {
	const apiConfig = new api.Configuration({
		basePath: new URL(process.env.NEXT_PUBLIC_PROPERTY_API).origin,
	});
	const q = new api.PropertyApi(apiConfig);
	const allProperties = (await q.apiPropertiesGet({})).hydramember;

	return {
		paths: allProperties
			.flatMap((property) => {
				if (!property.slug) return [];

				return property.slug;
			})
			.map((slug) => ({ params: { slug } })),
		fallback: "blocking",
	};
};
